import React, { useState } from 'react';
import { FiChevronLeft, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Input, alert, Modal } from 'equasion-lib';
import ZoneSelectWithScan from '@/lib/shared-coms/ZoneSelectWithScan';
import MobileAwareDatePicker from '@/lib/shared-coms/MobileAwareDatePicker';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { Formik, Form } from 'formik';
import { unserializedStockingValidation } from './UnserializedStocking.validation';
import './UnserializedStocking.scss';
import { useRootStore } from '@/lib/providers/RootProvider';

type ZoneMeta = { name: string; zoneType: string };

function UnserializedStocking({
  purchaseOrder,
  stockUnserializedPurchaseOrder,
  clientId,
  refetch
}) {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const navigate = useNavigate();
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [stockingQuantities, setStockingQuantities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zoneMeta, setZoneMeta] = useState<Record<string, ZoneMeta>>({});
  const {
    id: purchaseOrderId,
    clientPurchaseOrderNumber,
    vendor: { companyName, locations },
    orderItems
  } = purchaseOrder;
  const totalQuantity = orderItems.reduce((acc, item) => acc + item.quantity, 0);
  const orderItemStockingQuantitiesMap = orderItems.reduce((acc, item) => {
    acc[item.productId] = stockingQuantities.filter(
      (quantity) => quantity.productId === item.productId
    );
    return acc;
  }, {});
  const allItemsFullyStocked = orderItems.every((item) => {
    const totalStocked = stockingQuantities
      .filter((sq) => sq.productId === item.productId)
      .reduce((sum, sq) => sum + sq.quantity, 0);
    return totalStocked === item.quantity;
  });
  const quarantineLineCount = stockingQuantities.filter(
    (sq) =>
      sq.zoneType === 'quarantine' ||
      zoneMeta[sq.zoneId]?.zoneType === 'quarantine'
  ).length;
  const hasQuarantineLines = quarantineLineCount > 0;

  const getRemainingQuantity = (productId, isEditing) => {
    const orderItem = orderItems.find((item) => item.productId === productId);
    if (!orderItem) return 0;
    const alreadyStocked = stockingQuantities
      .filter((sq) => sq.productId === productId)
      .reduce((sum, sq) => sum + sq.quantity, 0);
    const editingQty = isEditing ? editingItem.quantity : 0;
    return orderItem.quantity - alreadyStocked + editingQty;
  };

  const resolveZoneType = (zoneId: string, explicit?: string) =>
    explicit || zoneMeta[zoneId]?.zoneType || 'stock';

  const handleDeleteLotExp = () => {
    if (!editingItem) return;
    setStockingQuantities(
      stockingQuantities.filter(
        (item) =>
          !(
            item.productId === editingItem.productId &&
            item.lot === editingItem.lot &&
            (item.expiration instanceof Date
              ? item.expiration
              : new Date(item.expiration)
            ).getTime() ===
              (editingItem.expiration instanceof Date
                ? editingItem.expiration
                : new Date(editingItem.expiration)
              ).getTime()
          )
      )
    );
    setEditingItem(null);
  };

  const submitStockUnserializedPurchaseOrder = async () => {
    const orderItemsByProduct = stockingQuantities.reduce((acc, item) => {
      acc[item.productId] = (acc[item.productId] || 0) + item.quantity;
      return acc;
    }, {});
    const orderItems = Object.entries(orderItemsByProduct).map(
      ([productId, quantity]) => ({
        productId,
        quantity
      })
    );
    if (orderItems.length === 0) return;
    setIsSubmitting(true);
    try {
      await stockUnserializedPurchaseOrder({
        variables: {
          id: purchaseOrderId,
          inventoryEvents: stockingQuantities.map(
            ({ productId, quantity, lot, expiration, zoneId }) => ({
              productId,
              quantity,
              lot,
              expiration,
              zoneId
            })
          ),
          companyId: companyId
        }
      });
      alert(
        hasQuarantineLines ? 'Stocked / Quarantined' : 'Stocked',
        hasQuarantineLines
          ? 'Products placed in stock and quarantine zones as selected'
          : 'Successfully stocked products',
        4,
        'success'
      );
      navigate('/unserialized?tab=stocking');
    } catch (e) {
      console.log(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page className="unserialized-receiving">
      <div className="action-top-bar">
        <Button
          type="secondary"
          iconBefore={<FiChevronLeft />}
          onClick={() => navigate('/unserialized?tab=stocking')}
        >
          Back
        </Button>
        <div className="order-details">
          <h1>Stocking</h1>
        </div>
      </div>
      <div className="content">
        <div className="order-details">
          <div className="order-number">
            <span>PO #{clientPurchaseOrderNumber}</span>
          </div>
          <div className="order-info">
            <span>
              {totalQuantity} {totalQuantity === 1 ? 'Item' : 'Items'}
            </span>
            <span>•</span>
            <span className="company-name">{companyName}</span>
          </div>
          <div></div>
        </div>
        <div className="order-items-list">
          <div className="flex space-between order-items-header">
            <span>
              {orderItems.length === 1
                ? `Item (${orderItems.length})`
                : `Items (${orderItems.length})`}
            </span>
            <span>Qty</span>
          </div>
          {orderItems.map(({ productId, quantity, product }) => {
            const stockingQuantities = orderItemStockingQuantitiesMap[productId] || [];
            return (
              <div key={productId}>
                <div>
                  <div>
                    <WarehouseProductDisplay product={product} />
                  </div>
                  <span className="quantity">{quantity}</span>
                </div>
                <div className="stocking-block">
                  {stockingQuantities.map(
                    ({ quantity: qty, lot, expiration, zoneId, zoneType }) => {
                      const isQuarantine =
                        resolveZoneType(zoneId, zoneType) === 'quarantine';
                      return (
                      <div
                        key={
                          lot +
                          (expiration instanceof Date
                            ? expiration.toISOString()
                            : String(expiration))
                        }
                        className={[
                          'stocking-quantity-row',
                          isQuarantine ? 'stocking-quantity-row--quarantine' : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() =>
                          setEditingItem({
                            productId,
                            quantity: qty,
                            lot,
                            expiration,
                            zoneId,
                            zoneType: resolveZoneType(zoneId, zoneType)
                          })
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setEditingItem({
                              productId,
                              quantity: qty,
                              lot,
                              expiration,
                              zoneId,
                              zoneType: resolveZoneType(zoneId, zoneType)
                            });
                          }
                        }}
                      >
                        <div className="stocking-quantity-cell">
                          <span className="label">Lot</span>
                          <span className="value">{lot}</span>
                        </div>
                        <div className="stocking-quantity-cell">
                          <span className="label">Expiration</span>
                          <span className="value">
                            {formatDateOnly(expiration)}
                          </span>
                        </div>
                        <div className="stocking-quantity-cell">
                          <span className="label">Zone</span>
                          <span className="value">
                            {zoneMeta[zoneId]?.name || zoneId}
                            {isQuarantine ? ' · Quarantine' : ''}
                          </span>
                        </div>
                        <div className="stocking-quantity-cell">
                          <span className="label">Qty</span>
                          <span className="value">{qty}</span>
                        </div>
                      </div>
                    );
                    }
                  )}
                  <div className="add-lot-exp-button-wrapper">
                    <Button
                      type="default"
                      iconBefore={<FiPlus />}
                      onClick={() => setSelectedProductId(productId)}
                      className="add-lot-exp-button"
                    >
                      Add Lot/Exp
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {hasQuarantineLines && (
          <div className="quarantine-stock-notice" role="status">
            <strong>Quarantine zone selected.</strong>
            <span>
              {quarantineLineCount === 1
                ? '1 line will be placed in quarantine status (not available to sell).'
                : `${quarantineLineCount} lines will be placed in quarantine status (not available to sell).`}
            </span>
          </div>
        )}
        <Button
          type="primary"
          fullWidth
          disabled={isSubmitting || !allItemsFullyStocked}
          onClick={submitStockUnserializedPurchaseOrder}
          className="stock-action-button"
        >
          {hasQuarantineLines ? 'Stock / Quarantine' : 'Stock'}
        </Button>
      </div>
      <Modal
        modalOpen={!!selectedProductId || !!editingItem}
        setModalOpen={() => {
          setSelectedProductId(null);
          setEditingItem(null);
        }}
      >
        <Formik
          key={editingItem ? 'edit' : 'add'}
          initialValues={
            editingItem
              ? {
                  lot: editingItem.lot,
                  expiration: editingItem.expiration,
                  zoneId: editingItem.zoneId,
                  quantity: editingItem.quantity
                }
              : {
                  lot: '',
                  expiration: new Date(),
                  zoneId: '',
                  quantity: 0
                }
          }
          validationSchema={unserializedStockingValidation(
            getRemainingQuantity(
              editingItem?.productId ?? selectedProductId,
              !!editingItem
            )
          )}
          validateOnBlur={false}
          onSubmit={(values) => {
            const productId = editingItem?.productId ?? selectedProductId;
            // Normalize expiration to Date (form may have string from locale date pickers)
            const expiration =
              values.expiration instanceof Date
                ? values.expiration
                : new Date(values.expiration);
            const zoneType = resolveZoneType(values.zoneId);
            const normalizedValues = {
              ...values,
              expiration,
              productId,
              zoneType
            };

            if (editingItem) {
              setStockingQuantities(
                stockingQuantities.map((item) =>
                  item.productId === editingItem.productId &&
                  item.lot === editingItem.lot &&
                  (item.expiration instanceof Date
                    ? item.expiration
                    : new Date(item.expiration)
                  ).getTime() ===
                    (editingItem.expiration instanceof Date
                      ? editingItem.expiration
                      : new Date(editingItem.expiration)
                    ).getTime()
                    ? normalizedValues
                    : item
                )
              );
              setEditingItem(null);
            } else {
              setStockingQuantities([...stockingQuantities, normalizedValues]);
              setSelectedProductId(null);
            }
          }}
        >
          {({ isSubmitting, values }) => {
            const selectedIsQuarantine =
              resolveZoneType(values.zoneId) === 'quarantine';
            return (
            <Form className="unserialized-receiving-form">
              <Input name="lot" label="Lot" />
              <MobileAwareDatePicker name="expiration" label="Expiration" />
              <ZoneSelectWithScan
                name="zoneId"
                label="Zone"
                companyId={clientId}
                scanTitle="Scan the stocking zone"
                scanHint="Scan the label on the zone this lot is being put away in."
                valueDisplay={zoneMeta[values.zoneId]?.name}
                onSelect={({ value, display, zoneType }) =>
                  setZoneMeta((prev) => ({
                    ...prev,
                    [value]: {
                      name: display.replace(/ \(Quarantine\)$/, ''),
                      zoneType: zoneType || 'stock'
                    }
                  }))
                }
              />
              {selectedIsQuarantine && (
                <div className="quarantine-zone-notice" role="status">
                  <strong>Quarantine zone</strong>
                  <span>
                    These items will be set to quarantine status and will not be
                    available to sell until stocked out of quarantine.
                  </span>
                </div>
              )}
              <Input
                name="quantity"
                label="Quantity"
                inputType="number"
                inputMode="numeric"
              />

              <Button type="primary" isSubmit disabled={isSubmitting} fullWidth>
                {editingItem ? 'Update' : 'Set'}
              </Button>
              {editingItem && (
                <Button
                  type="danger"
                  onClick={handleDeleteLotExp}
                  fullWidth
                  className="delete-lot-exp-button"
                >
                  Delete
                </Button>
              )}
            </Form>
            );
          }}
        </Formik>
      </Modal>
    </Page>
  );
}

export default UnserializedStocking;
